import React, { useEffect, useState } from 'react';
import {
	Stack,
	Popover,
	MenuItem,
	FormControlLabel,
	Checkbox,
	Button,
	Typography
} from '@mui/material';
import { ReactComponent as FilterIcon } from '../../assets/ml_summary_filter.svg';

const commonMenuItemStyles = {
	margin: 0,
	padding: 0,
	'& .MuiFormControlLabel-root': {
		margin: 0,
		width: '100%'
	},
	'& .MuiCheckbox-root': {
		padding: 0
	}
};

type MultiCheckFilterProps = {
	title: string;
	options: string[];
	storageKey?: string;
	checkedListRef: React.MutableRefObject<string[]>;
	handleOkClick: () => void;
	disable?:boolean
};

const MultiCheckFilter = ({
	title,
	options = [],
	storageKey,
	checkedListRef,
	handleOkClick,
	disable
}: MultiCheckFilterProps) => {
	const [filterMenu, setFilterMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
	const [checkedList, setCheckedList] = useState<string[]>([]);
	const [itemsChecked, setItemsChecked] = useState(false);

	useEffect(() => {
		if (storageKey) {
			const stored = localStorage.getItem(storageKey);
			if (stored) {
				const parsed = JSON.parse(stored);
				setCheckedList(parsed);
				checkedListRef.current = parsed;
				if (parsed.length === options.length) setItemsChecked(true);
			}
		}
	}, [options, storageKey]);

	const filterClick = (event: React.MouseEvent) => {
		event.preventDefault();
		setFilterMenu(filterMenu ? null : { mouseX: event.clientX - 20, mouseY: event.clientY - 6 });
	};

	const handleClose = () => {
		setFilterMenu(null);
		if (storageKey) {
			const stored = localStorage.getItem(storageKey);
			if (stored) {
				const parsed = JSON.parse(stored);
				setCheckedList(parsed);
				checkedListRef.current = parsed;
				setItemsChecked(parsed.length === options.length);
			}
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value, checked } = e.target;
		let updatedList: string[];
		if (checked) {
			updatedList = [...checkedList, value];
		} else {
			updatedList = checkedList.filter((item) => item !== value);
		}
		setCheckedList(updatedList);
		checkedListRef.current = updatedList;
		setItemsChecked(updatedList.length === options.length);
	};

	const selectAllCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { checked } = e.target;
		if (checked) {
			setCheckedList(options);
			checkedListRef.current = options;
		} else {
			setCheckedList([]);
			checkedListRef.current = [];
		}
		setItemsChecked(checked);
	};

	const handleOkChange = () => {
		if (storageKey) localStorage.setItem(storageKey, JSON.stringify(checkedList));
		handleOkClick();
		setFilterMenu(null);
	};

	return (
		<Stack sx={{...(disable && { pointerEvents: 'none', opacity: 0.5 })}}>
			<Stack direction="row" alignItems="center" sx={{ backgroundColor: 'primary.main', borderRadius: 1 }}>
				<Typography
					onClick={filterClick}
					style={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
					sx={{ lineHeight: '30px', px: 2, maxWidth: 100 }}
					variant='filterText'
				>
					{checkedListRef.current.length === options.length || checkedListRef.current.length === 0
						? `All ${title}`
						: checkedListRef.current.length === 1
						? checkedListRef.current[0]
						: `${checkedListRef.current.length} ${title}`}
				</Typography>
				<FilterIcon onClick={filterClick} style={{ cursor: 'pointer' }} />
			</Stack>

			<Popover
				open={filterMenu !== null}
				onClose={handleClose}
				anchorReference="anchorPosition"
				anchorPosition={filterMenu ? { top: filterMenu.mouseY, left: filterMenu.mouseX } : undefined}
			>
				<Stack spacing={1} sx={{ backgroundColor: 'primary.main', width: '230px' }}>
					<Stack sx={{ maxHeight: '140px', overflowY: 'auto' }}>
						<MenuItem disableGutters sx={{ ...commonMenuItemStyles, borderBottom: '1px solid #009BD8' }}>
							<FormControlLabel
								control={
									<Checkbox color="success" value="all" checked={itemsChecked} onChange={selectAllCheck} />
								}
								label={<Typography variant="filterText">All</Typography>}
							/>
						</MenuItem>

						{options
							.sort((a, b) => {
								const aChecked = checkedList.includes(a);
								const bChecked = checkedList.includes(b);
								if (aChecked && !bChecked) return -1;
								if (!aChecked && bChecked) return 1;
								return a.localeCompare(b);
							})
							.map((opt) => (
								<MenuItem key={opt} disableGutters sx={commonMenuItemStyles}>
									<FormControlLabel
										control={
											<Checkbox
												color="success"
												value={opt}
												checked={checkedList.includes(opt)}
												onChange={handleChange}
											/>
										}
										label={<Typography variant="filterText">{opt}</Typography>}
									/>
								</MenuItem>
							))}
					</Stack>

					<Stack direction="row" justifyContent="flex-end" spacing={1}>
						<Button variant="pepwiseSecondaryOutline" onClick={handleOkChange}>
							OK
						</Button>
						<Button variant="pepwiseSecondaryOutline" onClick={handleClose}>
							Cancel
						</Button>
					</Stack>
				</Stack>
			</Popover>
		</Stack>
	);
};

export default MultiCheckFilter;